import React, { useEffect, useMemo, useRef } from "react";

const PREFIX = "BLRT1:";
const ALLOWED_BLOCKS = new Set(["p", "h2", "h3", "ul", "ol"]);

const cleanText = (value) =>
  String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");

const sameMarks = (a, b) =>
  !!a &&
  !!b &&
  !!a.bold === !!b.bold &&
  !!a.italic === !!b.italic &&
  !!a.underline === !!b.underline;

const compactRuns = (runs) => {
  const out = [];

  (Array.isArray(runs) ? runs : []).forEach((run) => {
    const text = cleanText(run?.text);
    if (!text) return;

    const normalized = {
      text,
      bold: !!run?.bold,
      italic: !!run?.italic,
      underline: !!run?.underline,
    };

    const previous = out[out.length - 1];

    if (previous && sameMarks(previous, normalized)) {
      previous.text += normalized.text;
    } else {
      out.push(normalized);
    }
  });

  return out;
};

const textOfRuns = (runs) =>
  compactRuns(runs)
    .map((run) => run.text)
    .join("");

const plainTextToDocument = (value) => {
  const lines = cleanText(value).split("\n");
  const blocks = [];
  let list = null;

  const flushList = () => {
    if (list && list.items.length) blocks.push(list);
    list = null;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushList();
      return;
    }

    const bullet = line.match(/^(?:[•●▪◦*-])\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);

    if (bullet) {
      if (!list || list.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }

      list.items.push([
        {
          text: bullet[1],
          bold: false,
          italic: false,
          underline: false,
        },
      ]);
      return;
    }

    if (numbered) {
      if (!list || list.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }

      list.items.push([
        {
          text: numbered[1],
          bold: false,
          italic: false,
          underline: false,
        },
      ]);
      return;
    }

    flushList();

    blocks.push({
      type: "p",
      runs: [
        {
          text: line,
          bold: false,
          italic: false,
          underline: false,
        },
      ],
    });
  });

  flushList();

  return {
    version: 1,
    blocks: blocks.length
      ? blocks
      : [{ type: "p", runs: [{ text: "", bold: false, italic: false, underline: false }] }],
  };
};

const parseStoredDocument = (richValue, fallbackText = "") => {
  const raw = String(richValue || "").trim();

  if (raw.startsWith(PREFIX)) {
    try {
      const parsed = JSON.parse(raw.slice(PREFIX.length));

      if (parsed?.version === 1 && Array.isArray(parsed.blocks)) {
        return {
          version: 1,
          blocks: parsed.blocks.filter((block) => ALLOWED_BLOCKS.has(block?.type)),
        };
      }
    } catch (_) {
      // fallback al testo semplice
    }
  }

  return plainTextToDocument(fallbackText);
};

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const runsToHtml = (runs) =>
  compactRuns(runs)
    .map((run) => {
      let html = escapeHtml(run.text).replace(/\n/g, "<br>");

      if (run.bold) html = `<strong>${html}</strong>`;
      if (run.italic) html = `<em>${html}</em>`;
      if (run.underline) html = `<u>${html}</u>`;

      return html;
    })
    .join("");

const documentToHtml = (doc) => {
  const blocks = Array.isArray(doc?.blocks) ? doc.blocks : [];

  return blocks
    .map((block) => {
      if (block.type === "ul" || block.type === "ol") {
        const tag = block.type;
        const items = Array.isArray(block.items) ? block.items : [];

        return `<${tag}>${items
          .map((item) => `<li>${runsToHtml(item)}</li>`)
          .join("")}</${tag}>`;
      }

      const type =
        block.type === "h2" ? "h2" : block.type === "h3" ? "h3" : "p";

      return `<${type}>${runsToHtml(block.runs)}</${type}>`;
    })
    .join("");
};

const documentToPlainText = (doc) => {
  const lines = [];

  (Array.isArray(doc?.blocks) ? doc.blocks : []).forEach((block) => {
    if (block.type === "ul" || block.type === "ol") {
      (Array.isArray(block.items) ? block.items : []).forEach((item, index) => {
        const prefix = block.type === "ol" ? `${index + 1}. ` : "• ";
        lines.push(prefix + textOfRuns(item).trim());
      });

      lines.push("");
      return;
    }

    const text = textOfRuns(block.runs).trim();

    if (text) {
      lines.push(text);
      lines.push("");
    }
  });

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

const serializeDocument = (doc) =>
  PREFIX +
  JSON.stringify({
    version: 1,
    blocks: Array.isArray(doc?.blocks) ? doc.blocks : [],
  });

const marksFromElement = (element, inherited) => {
  const marks = { ...inherited };
  const tag = element?.tagName?.toLowerCase?.() || "";
  const style = element?.style || {};

  if (tag === "b" || tag === "strong") marks.bold = true;
  if (tag === "i" || tag === "em") marks.italic = true;
  if (tag === "u") marks.underline = true;

  const weight = String(style.fontWeight || "").toLowerCase();
  if (weight === "bold" || Number.parseInt(weight, 10) >= 600) {
    marks.bold = true;
  }

  if (String(style.fontStyle || "").toLowerCase() === "italic") {
    marks.italic = true;
  }

  if (String(style.textDecoration || "").toLowerCase().includes("underline")) {
    marks.underline = true;
  }

  return marks;
};

const nodeToRuns = (node, inherited = {}) => {
  if (!node) return [];

  if (node.nodeType === Node.TEXT_NODE) {
    const text = cleanText(node.nodeValue);
    if (!text) return [];

    return [
      {
        text,
        bold: !!inherited.bold,
        italic: !!inherited.italic,
        underline: !!inherited.underline,
      },
    ];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const tag = node.tagName.toLowerCase();

  if (tag === "br") {
    return [
      {
        text: "\n",
        bold: !!inherited.bold,
        italic: !!inherited.italic,
        underline: !!inherited.underline,
      },
    ];
  }

  const marks = marksFromElement(node, inherited);
  const runs = [];

  Array.from(node.childNodes).forEach((child) => {
    runs.push(...nodeToRuns(child, marks));
  });

  return compactRuns(runs);
};

const normalizeParagraphBlocks = (blocks) => {
  const out = [];
  let pendingList = null;

  const flush = () => {
    if (pendingList && pendingList.items.length) out.push(pendingList);
    pendingList = null;
  };

  blocks.forEach((block) => {
    if (block.type !== "p") {
      flush();
      out.push(block);
      return;
    }

    const plain = textOfRuns(block.runs).trim();
    const bullet = plain.match(/^(?:[•●▪◦*-])\s+(.*)$/);
    const numbered = plain.match(/^\d+[.)]\s+(.*)$/);

    if (!bullet && !numbered) {
      flush();
      out.push(block);
      return;
    }

    const type = bullet ? "ul" : "ol";
    const stripped = bullet ? bullet[1] : numbered[1];

    if (!pendingList || pendingList.type !== type) {
      flush();
      pendingList = { type, items: [] };
    }

    pendingList.items.push([
      {
        text: stripped,
        bold: false,
        italic: false,
        underline: false,
      },
    ]);
  });

  flush();
  return out;
};

const rootToDocument = (root) => {
  if (!root) return plainTextToDocument("");

  const blocks = [];

  const appendElement = (element) => {
    const tag = element.tagName?.toLowerCase?.() || "";

    if (tag === "ul" || tag === "ol") {
      const items = Array.from(element.children)
        .filter((child) => child.tagName?.toLowerCase() === "li")
        .map((li) => compactRuns(nodeToRuns(li)))
        .filter((runs) => textOfRuns(runs).trim());

      if (items.length) blocks.push({ type: tag, items });
      return;
    }

    if (["h1", "h2", "h3", "h4"].includes(tag)) {
      const runs = compactRuns(nodeToRuns(element));
      if (textOfRuns(runs).trim()) {
        blocks.push({
          type: tag === "h1" || tag === "h2" ? "h2" : "h3",
          runs,
        });
      }
      return;
    }

    if (tag === "p" || tag === "div") {
      const nestedLists = Array.from(element.children).filter((child) =>
        ["ul", "ol"].includes(child.tagName?.toLowerCase())
      );

      if (nestedLists.length) {
        const clone = element.cloneNode(true);
        Array.from(clone.querySelectorAll("ul,ol")).forEach((node) =>
          node.remove()
        );

        const runs = compactRuns(nodeToRuns(clone));
        if (textOfRuns(runs).trim()) blocks.push({ type: "p", runs });

        nestedLists.forEach(appendElement);
        return;
      }

      const runs = compactRuns(nodeToRuns(element));
      if (textOfRuns(runs).trim()) blocks.push({ type: "p", runs });
      return;
    }

    const runs = compactRuns(nodeToRuns(element));
    if (textOfRuns(runs).trim()) blocks.push({ type: "p", runs });
  };

  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = cleanText(node.nodeValue).trim();

      if (text) {
        blocks.push({
          type: "p",
          runs: [{ text, bold: false, italic: false, underline: false }],
        });
      }

      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) appendElement(node);
  });

  return {
    version: 1,
    blocks: normalizeParagraphBlocks(blocks),
  };
};

const htmlToSafeDocument = (html, plainText) => {
  if (!html) return plainTextToDocument(plainText);

  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    return rootToDocument(parsed.body);
  } catch (_) {
    return plainTextToDocument(plainText);
  }
};

const buttonStyle = {
  minWidth: "34px",
  height: "30px",
  padding: "0 9px",
  border: "1px solid #cbd5df",
  borderRadius: "5px",
  background: "#ffffff",
  color: "#243447",
  fontWeight: 600,
  cursor: "pointer",
};

const RichTextDescriptionEditor = ({
  value,
  fallbackText,
  onChange,
  minHeight = 300,
}) => {
  const editorRef = useRef(null);

  const initialHtml = useMemo(
    () => documentToHtml(parseStoredDocument(value, fallbackText)),
    [value, fallbackText]
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (document.activeElement !== editor && editor.innerHTML !== initialHtml) {
      editor.innerHTML = initialHtml;
    }
  }, [initialHtml]);

  const emit = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const doc = rootToDocument(editor);
    const rich = serializeDocument(doc);
    const plain = documentToPlainText(doc);

    onChange?.({ rich, plain });
  };

  const command = (name, commandValue = null) => {
    editorRef.current?.focus();

    try {
      document.execCommand(name, false, commandValue);
    } catch (_) {
      // browser legacy fallback: nessuna eccezione verso l'utente
    }

    emit();
  };

  const onPaste = (event) => {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    event.preventDefault();

    const html = clipboard.getData("text/html");
    const plain = clipboard.getData("text/plain");
    const safeDoc = htmlToSafeDocument(html, plain);
    const safeHtml = documentToHtml(safeDoc);

    editorRef.current?.focus();

    try {
      document.execCommand("insertHTML", false, safeHtml);
    } catch (_) {
      document.execCommand("insertText", false, documentToPlainText(safeDoc));
    }

    emit();
  };

  const toolbarButton = (label, title, handler, extraStyle = {}) => (
    <button
      type="button"
      title={title}
      onMouseDown={(event) => {
        event.preventDefault();
        handler();
      }}
      style={{ ...buttonStyle, ...extraStyle }}
    >
      {label}
    </button>
  );

  return (
    <div
      data-betalumina-rich-description="1"
      style={{
        border: "1px solid #cbd5df",
        borderRadius: "8px",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          alignItems: "center",
          padding: "8px",
          background: "#f5f7f9",
          borderBottom: "1px solid #d8e0e7",
        }}
      >
        {toolbarButton("B", "Grassetto", () => command("bold"), {
          fontWeight: 800,
        })}
        {toolbarButton("I", "Corsivo", () => command("italic"), {
          fontStyle: "italic",
        })}
        {toolbarButton("U", "Sottolineato", () => command("underline"), {
          textDecoration: "underline",
        })}

        <span
          aria-hidden="true"
          style={{
            width: "1px",
            height: "22px",
            background: "#d2dae2",
            margin: "0 2px",
          }}
        />

        {toolbarButton("Titolo", "Titolo principale", () =>
          command("formatBlock", "h2")
        )}
        {toolbarButton("Sottotitolo", "Sottotitolo", () =>
          command("formatBlock", "h3")
        )}
        {toolbarButton("Testo", "Paragrafo normale", () =>
          command("formatBlock", "p")
        )}

        <span
          aria-hidden="true"
          style={{
            width: "1px",
            height: "22px",
            background: "#d2dae2",
            margin: "0 2px",
          }}
        />

        {toolbarButton("• Elenco", "Elenco puntato", () =>
          command("insertUnorderedList")
        )}
        {toolbarButton("1. Elenco", "Elenco numerato", () =>
          command("insertOrderedList")
        )}
        {toolbarButton("Pulisci", "Rimuovi formattazione carattere", () =>
          command("removeFormat")
        )}
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        style={{
          minHeight,
          padding: "14px 16px",
          outline: "none",
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          lineHeight: 1.55,
          color: "#25364a",
          overflowY: "auto",
          whiteSpace: "normal",
        }}
      />

      <div
        style={{
          padding: "7px 10px",
          borderTop: "1px solid #e2e8ee",
          background: "#fbfcfd",
          color: "#667788",
          fontSize: "11.5px",
          lineHeight: 1.4,
        }}
      >
        Incolla da ChatGPT, Word, Google Docs o pagine web: vengono mantenuti
        titoli, grassetti, corsivi, sottolineature, paragrafi ed elenchi.
        Font, colori e stili esterni vengono normalizzati nello stile BetaLumina.
      </div>
    </div>
  );
};

export default RichTextDescriptionEditor;
